/* eslint-disable no-plusplus */
// eslint-disable-next-line import/no-webpack-loader-syntax
import '!style-loader!css-loader!react-international-phone/style.css';

import React, { useState, useRef, useEffect } from 'react';
import { ChangeAction } from 'redux-form';
import { defaultCountries, FlagImage } from 'react-international-phone';
import produce from 'immer';
import cn from 'classnames';

import type { CardFlowEvent } from 'types/story';
import { Context as ThemeContext } from 'admin/components/common/Theme';
import { Select } from 'src/routes/admin/components/common/Form';
import { Icon } from 'src/routes/admin/components/common/Icon';
import Button from 'src/routes/admin/components/common/Button';
import Text from 'admin/components/common/Text';
import { CardEventFactory } from 'src/common/utils/facades/templates/card-event';
import css from './EditCardModal.scss';

type Props = {
	events: CardFlowEvent[];
	change: ChangeAction;
};

const DEFAULT_EVENT_PROPS = {
	name: 'Unknown',
	countryIso2: '',
} as const;

const options = defaultCountries.map(([name, iso2]) => ({
	value: name,
	label: (
		<div className={css.countryFlag}>
			<FlagImage iso2={iso2} />
			{name}
		</div>
	),
}));

const getIso2ByCountryName = (countryName: string) => {
	const entry = defaultCountries.find(([name, code]) => name === countryName);
	return entry ? entry[1] : undefined;
};

const GeolocationEvents: React.FC<Props> = props => {
	const { events: initialEvents, change } = props;
	const context = React.useContext(ThemeContext);
	const [events, setEvents] = useState(() => {
		// There is always the "Default" event which we don't show the the user
		return initialEvents.length > 1
			? initialEvents
			: [...initialEvents, CardEventFactory.create('nav-geolocation', { ...DEFAULT_EVENT_PROPS })];
	});
	const lastSelectWrapperRef = useRef<HTMLDivElement>(null);
	const shouldFocusLastSelect = useRef(false);

	useEffect(() => {
		if (shouldFocusLastSelect.current && lastSelectWrapperRef.current) {
			const input = lastSelectWrapperRef.current.querySelector<HTMLInputElement>('input');
			if (input) {
				setTimeout(() => {
					input.focus();
				}, 0);
				shouldFocusLastSelect.current = false;
			}
		}
	}, [events]);

	const addEvent = () => {
		const nextEvents = [...events, CardEventFactory.create('nav-geolocation', { ...DEFAULT_EVENT_PROPS })];

		setEvents(nextEvents);
		change('events', nextEvents);
		shouldFocusLastSelect.current = true;
	};

	const updateEvent = (index: number, countryName: string) => {
		const countryIso2 = getIso2ByCountryName(countryName);
		const nextEvents = produce(events, draft => {
			draft[index].countryIso2 = countryIso2;
			draft[index].name = countryName;
		});

		setEvents(nextEvents);
		change('events', nextEvents);
	};

	const removeEvent = (index: number) => {
		const nextEvents = events.filter((_, i) => i !== index);

		if (nextEvents.length <= 1) {
			nextEvents.push(CardEventFactory.create('nav-geolocation', { ...DEFAULT_EVENT_PROPS }));
		}

		setEvents(nextEvents);
		change('events', nextEvents);
	};

	const totalEvents = events.length;
	const canAddEvent = events.at(-1)?.name !== DEFAULT_EVENT_PROPS.name;

	return (
		<div className={css.geolocationEvents}>
			<Text size="label" weight={Text.weight.bold} text="Countries:" />
			{events.map((event, index) => {
				if (index === 0) {
					// We don't show "Default" event to the user, this is intentionaly
					return null;
				}

				const isLastSelect = index === totalEvents - 1;

				// Filter out already selected countries (except the current one)
				const selectedCountryNames = events.filter((e, i) => i !== index && i !== 0 && e.name).map(e => e.name);
				const availableOptions = options.filter(
					option => !selectedCountryNames.includes(option.value) || option.value === event.name
				);

				return (
					<div
						className={css.countrySelector}
						key={`geolocation-event-${event._id}`}
						ref={isLastSelect ? lastSelectWrapperRef : undefined}
					>
						<Select
							value={event.name}
							options={availableOptions}
							theme={context.theme}
							eventListeners={{
								onChange: (countryName: string) => updateEvent(index, countryName),
							}}
							menuPlacement="bottom"
							maxMenuHeight={200}
							isSearchable
							className={cn(css.field, event.countryIso2 && css.hasGeolocationValue)}
						/>
						<button
							type="button"
							className={css.removeBtn}
							onClick={() => {
								removeEvent(index);
							}}
						>
							<Icon className={css.removeBtnIcon} type="minus-rounded" />
						</button>
					</div>
				);
			})}
			<Button className={css.addBtn} view="secondary-gray" onClick={() => addEvent()} disabled={!canAddEvent}>
				<div className={css.addBtnLabel}>
					<Icon className={css.addBtnLabelIcon} type="plus-rounded" />
					<Text size="label" weight="normal" text="Add Country" />
				</div>
			</Button>
		</div>
	);
};

export default GeolocationEvents;
